import React from 'react';
import UnifiedSimpleConversionExplanation from './UnifiedSimpleConversionExplanation';

const VoltageConversionExplanation = ({
  fromUnit,
  toUnit,
  inputValue,
  result,
  darkMode
}) => {
  const getConversionData = () => {
    // Volts to other units
    if (fromUnit === 'Volts' && toUnit === 'Kilovolts') {
      return {
        formula: 'kV = V ÷ 1000',
        conversionSteps: [
          `Take the voltage in volts: ${inputValue}V`,
          `Divide by 1000: ${inputValue} ÷ 1000 = ${result}kV`
        ],
        realWorldExample: "High-voltage power lines typically operate at 100-750 kilovolts.",
        funFacts: [
          "The volt is named after Alessandro Volta, inventor of the electric battery.",
          "A typical car battery provides 12 volts DC.",
          "Lightning can create voltage differences of millions of volts.",
        ]
      };
    }

    if (fromUnit === 'Volts' && toUnit === 'Millivolts') {
      return {
        formula: 'mV = V × 1000',
        conversionSteps: [
          `Take the voltage in volts: ${inputValue}V`,
          `Multiply by 1000: ${inputValue} × 1000 = ${result}mV`
        ],
        realWorldExample: "Neural signals in the human body are typically measured in millivolts.",
        funFacts: [
          "EEG signals from the brain are typically less than 100 microvolts.",
          "A typical AA battery produces 1500 millivolts (1.5V).",
          "Some sensors operate with just a few millivolts.",
        ]
      };
    }

    if (fromUnit === 'Kilovolts' && toUnit === 'Megavolts') {
      return {
        formula: 'MV = kV ÷ 1000',
        conversionSteps: [
          `Take the voltage in kilovolts: ${inputValue}kV`,
          `Divide by 1000: ${inputValue} ÷ 1000 = ${result}MV`
        ],
        realWorldExample: "Large particle accelerators can operate at several megavolts.",
        funFacts: [
          "Van de Graaff generators can produce voltages up to 5 megavolts.",
          "Lightning can exceed 100 megavolts.",
          "High-voltage direct current (HVDC) transmission can reach 1.1 megavolts.",
        ]
      };
    }

    if (fromUnit === 'Microvolts' && toUnit === 'Millivolts') {
      return {
        formula: 'mV = µV ÷ 1000',
        conversionSteps: [
          `Take the voltage in microvolts: ${inputValue}µV`,
          `Divide by 1000: ${inputValue} ÷ 1000 = ${result}mV`
        ],
        realWorldExample: "Medical ECG signals range from 100 microvolts to 4 millivolts.",
        funFacts: [
          "Microvolts are commonly used in biomedical measurements.",
          "Some sensitive scientific instruments can detect nanovolts (1000 times smaller than microvolts).",
          "Thermal noise in electronics is often measured in microvolts.",
        ]
      };
    }

    // Special case for EMU and ESU conversions
    if (fromUnit === 'EMU of electric potential' || toUnit === 'ESU of electric potential') {
      return {
        formula: 'Special conversion factors apply for electromagnetic units',
        conversionSteps: [
          `Start with ${inputValue} ${fromUnit}`,
          `Apply appropriate conversion factor`,
          `Result: ${result} ${toUnit}`
        ],
        realWorldExample: "EMU and ESU units are mainly used in theoretical physics calculations.",
        funFacts: [
          "EMU (electromagnetic units) were historically used in magnetic calculations.",
          "ESU (electrostatic units) were part of the CGS system of units.",
          "Modern physics typically uses SI units (volts) instead of EMU or ESU.",
        ]
      };
    }

    // Default case for other conversions
    return {
      formula: `Converting from ${fromUnit} to ${toUnit}`,
      conversionSteps: [
        `Start with ${inputValue} ${fromUnit}`,
        `Apply conversion factor`,
        `Result: ${result} ${toUnit}`
      ],
      realWorldExample: "Voltage measurements are crucial in electronics, power systems, and scientific research.",
      funFacts: [
        "Voltage is actually a measure of electric potential difference.",
        "The SI unit for voltage (volt) is defined as joules per coulomb.",
        "Static electricity can create voltage differences of thousands of volts.",
      ]
    };
  };

  const conversionData = getConversionData();

  return (
    <UnifiedSimpleConversionExplanation
      conversionType="voltage"
      fromUnit={fromUnit}
      toUnit={toUnit}
      inputValue={inputValue}
      result={result}
      darkMode={darkMode}
      {...conversionData}
    />
  );
};

export default VoltageConversionExplanation; 